import { useContext, useState, useEffect, useCallback, useMemo } from 'react'
import { PlanCuentasApi } from '../../../../../../../api/planCuentas.api'
import SearchInput from '../../../../../ui/SearchInput'
import FolderItem, { structureFolders } from './Folders/FolderItem'
import translations from '../../../../../ui/traslation/translationPLanDeCuentas.json'
import Switch from '@mui/material/Switch'
import GlobalContext from '../../../../../../../context/global-context'

// Debounce hook
function useDebounce(value, delay = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(handler)
  }, [value, delay])
  return debounced
}

// Utils
const getAllParentCodes = codigoFinal => {
  if (!codigoFinal || typeof codigoFinal !== 'string') return []
  const codes = []
  const parts = codigoFinal.split('-').filter(p => p !== '0')
  for (let i = parts.length; i > 0; i--) {
    const zeros = Math.max(4 - i, 0)
    codes.push(parts.slice(0, i).join('-') + '-0'.repeat(zeros))
  }
  return codes
}

const flattenFolders = folders => {
  return folders.reduce((acc, folder) => {
    acc.push(folder)
    if (folder.subfolders?.length) acc.push(...flattenFolders(folder.subfolders))
    return acc
  }, [])
}

const filterFoldersBySearchTerm = (folders, term) => {
  if (!term) return folders
  const lower = term.toLowerCase()
  return folders.filter(
    f => f.nameFinal?.toLowerCase().includes(lower) || f.codigoFinal?.toLowerCase().includes(lower)
  )
}

const PlanDeCuentas = () => {
  const { currentBusiness } = useContext(GlobalContext)
  const [folders, setFolders] = useState([])
  const [isEditMode, setIsEditMode] = useState(false)
  const [showDeleted, setShowDeleted] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const api = useMemo(() => new PlanCuentasApi(), [])

  const fetchPlanCuentas = useCallback(async () => {
    if (!currentBusiness) return
    setIsLoading(true)
    setError(null)
    try {
      const data = await api.getPlanCuentasByBusinessId(currentBusiness.id)
      const cleaned = data.filter(
        item => item?.codigoFinal && !String(item.codigoFinal).endsWith('-98')
      )
      setFolders(structureFolders(cleaned, showDeleted))
    } catch (err) {
      console.error(translations.errors.fetchPlanError, err)
      setError(translations.errors.fetchPlanError)
    } finally {
      setIsLoading(false)
    }
  }, [api, currentBusiness, showDeleted])

  useEffect(() => {
    fetchPlanCuentas()
  }, [fetchPlanCuentas])

  const debouncedSearchTerm = useDebounce(searchTerm)

  const allFolders = useMemo(() => flattenFolders(folders), [folders])
  const visibleFolders = useMemo(
    () => allFolders.filter(f => showDeleted || f.isDeleted !== '1'),
    [allFolders, showDeleted]
  )
  const searchedFolders = useMemo(
    () => filterFoldersBySearchTerm(visibleFolders, debouncedSearchTerm),
    [visibleFolders, debouncedSearchTerm]
  )

  const requiredCodes = useMemo(() => {
    const codes = new Set()
    searchedFolders.forEach(f => getAllParentCodes(f.codigoFinal).forEach(c => codes.add(c)))
    return codes
  }, [searchedFolders])

  const finalData = useMemo(
    () => allFolders.filter(f => requiredCodes.has(f.codigoFinal) || searchedFolders.includes(f)),
    [allFolders, requiredCodes, searchedFolders]
  )
  const finalFolders = useMemo(
    () => structureFolders(finalData, showDeleted),
    [finalData, showDeleted]
  )

  const handleToggleEditMode = useCallback(() => {
    setIsEditMode(prev => {
      const next = !prev
      if (!next) setShowDeleted(false)
      return next
    })
  }, [])
  const handleToggleShowDeleted = useCallback(() => setShowDeleted(prev => !prev), [])
  const onSearchChange = useCallback(e => setSearchTerm(e.target.value), [])
  const onSearchKeyDown = useCallback(e => e.key === 'Enter' && setSearchTerm(e.target.value), [])

  return (
    <div className='relative font-sans'>
      {/* Actions bar */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'>
        <SearchInput
          placeholder={translations.actions.search}
          classNameBorder='rounded-full text-[0.75rem] font-medium text-text placeholder:text-muted'
          onChange={onSearchChange}
          onKeyDown={onSearchKeyDown}
          value={searchTerm}
        />
        <div className='flex flex-wrap items-center gap-4'>
          <div className='flex items-center gap-3'>
            <span className='text-xs font-semibold uppercase tracking-wider text-muted'>
              {translations.actions.edit}
            </span>
            <Switch checked={isEditMode} onChange={handleToggleEditMode} color='primary' />
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-xs font-semibold uppercase tracking-wider text-muted'>
              {translations.actions.restore}
            </span>
            <Switch
              checked={showDeleted}
              onChange={handleToggleShowDeleted}
              disabled={!isEditMode}
              color='secondary'
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {error ? (
        <p className='text-red-600'>{error}</p>
      ) : (
        <div className='relative'>
          {isLoading && (
            <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm pointer-events-none'>
              <div className='flex items-center space-x-3 text-gray-700'>
                <div className='h-8 w-8 border-4 border-accent1 border-t-transparent rounded-full animate-spin' />
                <span>Cargando...</span>
              </div>
            </div>
          )}

          {finalFolders.length > 0 ? (
            <div className='space-y-3'>
              {finalFolders.map(folder => (
                <FolderItem
                  key={folder.cuentaContableId}
                  folder={folder}
                  isEditMode={isEditMode}
                  businessId={currentBusiness?.id}
                  refreshData={fetchPlanCuentas}
                />
              ))}
            </div>
          ) : (
            <p>{translations.actions.noData}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default PlanDeCuentas
